/**
 * BoardSettings — board configuration panel for managing title, description,
 * color, and labels. Member management has been moved to the ShareModal.
 *
 * Requirements: 9.1, 9.2, 9.3, 9.4
 */

import React, { useCallback, useEffect, useState } from 'react';
import * as api from '../services/api-client';
import { NotificationTemplateEditor } from './board/NotificationTemplateEditor';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { CustomFieldsAdmin } from './board/CustomFieldsAdmin';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BoardLabel {
  readonly labelId: string;
  readonly name: string;
  readonly color: string;
}

export interface BoardSettingsProps {
  readonly boardId: string;
  readonly title: string;
  readonly description: string;
  readonly color: string;
  readonly visibility?: string;
  readonly labels: readonly BoardLabel[];
  readonly onClose: () => void;
  readonly onBoardUpdated: (updates: Partial<{ title: string; description: string; color: string; labels: BoardLabel[] }>) => void;
  readonly onBoardArchived?: () => void;
  readonly userRole?: string;
}

// ---------------------------------------------------------------------------
// Teams config types
// ---------------------------------------------------------------------------

interface TeamsConfigResponse {
  readonly source: 'board_override' | 'global_default' | 'none';
  readonly teamId?: string;
  readonly teamDisplayName?: string;
  readonly channelId?: string;
  readonly channelDisplayName?: string;
  readonly legacy?: boolean;
  readonly configuredBy?: string;
  readonly configuredAt?: string;
}

interface TeamOption {
  readonly id: string;
  readonly displayName: string;
}

interface ChannelOption {
  readonly id: string;
  readonly displayName: string;
}

// ---------------------------------------------------------------------------
// Label color palette
// ---------------------------------------------------------------------------

const LABEL_COLORS = [
  { name: 'Green', value: '#22c55e' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Sky', value: '#0ea5e9' },
  { name: 'Black', value: '#1f2937' },
  { name: 'Lime', value: '#84cc16' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BoardSettings({
  boardId,
  title,
  description,
  color,
  visibility,
  labels,
  onClose,
  onBoardUpdated,
  onBoardArchived,
  userRole,
}: BoardSettingsProps): React.ReactElement {
  const isAdmin = userRole === 'admin';

  // Board field state
  const [boardTitle, setBoardTitle] = useState(title);
  const [boardDescription, setBoardDescription] = useState(description);
  const [boardColor, setBoardColor] = useState(color);

  // Label editing state
  const [editingLabelId, setEditingLabelId] = useState<string | null>(null);
  const [labelName, setLabelName] = useState('');
  const [labelColor, setLabelColor] = useState(LABEL_COLORS[0]!.value);
  const [showCreateLabel, setShowCreateLabel] = useState(false);

  // Archive confirmation state
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  // Teams notification config state
  const [teamsConfig, setTeamsConfig] = useState<TeamsConfigResponse | null>(null);
  const [availableTeams, setAvailableTeams] = useState<TeamOption[]>([]);
  const [availableChannels, setAvailableChannels] = useState<ChannelOption[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [selectedTeamName, setSelectedTeamName] = useState('');
  const [selectedChannelId, setSelectedChannelId] = useState('');
  const [selectedChannelName, setSelectedChannelName] = useState('');
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [teamsSaving, setTeamsSaving] = useState(false);
  const [teamsTesting, setTeamsTesting] = useState(false);
  const [teamsMessage, setTeamsMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [pickerError, setPickerError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Fetch Teams config on mount
  // ---------------------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;
    async function fetchTeamsConfig() {
      try {
        const config = await api.get<TeamsConfigResponse>(`/v1/boards/${boardId}/teams-config`);
        if (cancelled) return;
        setTeamsConfig(config);
        if (config.source === 'board_override') {
          setSelectedTeamId(config.teamId ?? '');
          setSelectedTeamName(config.teamDisplayName ?? '');
          setSelectedChannelId(config.channelId ?? '');
          setSelectedChannelName(config.channelDisplayName ?? '');
        }
      } catch {
        // Fetch failed — leave teams config as null
      }
    }
    fetchTeamsConfig();
    return () => { cancelled = true; };
  }, [boardId]);

  // ---------------------------------------------------------------------------
  // Load available teams for picker (admin only)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    async function fetchTeams() {
      setTeamsLoading(true);
      setPickerError(null);
      try {
        const result = await api.get<{ teams: TeamOption[] }>(`/v1/boards/${boardId}/teams-config/teams`);
        if (cancelled) return;
        setAvailableTeams(result.teams);
      } catch (err: unknown) {
        if (cancelled) return;
        const apiErr = err && typeof err === 'object' && 'code' in err ? (err as { code: string; message: string }) : null;
        if (apiErr?.code === 'TEAMS_NOT_CONFIGURED') {
          setPickerError('A system administrator must complete the Teams integration setup before channels can be selected.');
        } else if (apiErr?.code === 'AUTH_FAILED') {
          setPickerError('Teams could not be loaded due to an authentication problem.');
        } else if (apiErr?.code === 'TEAMS_TIMEOUT') {
          setPickerError('Teams could not be loaded due to a timeout.');
        } else {
          setPickerError('Teams could not be loaded due to a service error.');
        }
      } finally {
        if (!cancelled) setTeamsLoading(false);
      }
    }
    fetchTeams();
    return () => { cancelled = true; };
  }, [boardId, isAdmin]);

  // ---------------------------------------------------------------------------
  // Load channels when team is selected
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!isAdmin || !selectedTeamId) {
      setAvailableChannels([]);
      return;
    }
    let cancelled = false;
    async function fetchChannels() {
      setChannelsLoading(true);
      setPickerError(null);
      try {
        const result = await api.get<{ channels: ChannelOption[] }>(`/v1/boards/${boardId}/teams-config/teams/${selectedTeamId}/channels`);
        if (cancelled) return;
        setAvailableChannels(result.channels);
      } catch (err: unknown) {
        if (cancelled) return;
        const apiErr = err && typeof err === 'object' && 'code' in err ? (err as { code: string; message: string }) : null;
        if (apiErr?.code === 'AUTH_FAILED') {
          setPickerError('Channels could not be loaded due to an authentication problem.');
        } else if (apiErr?.code === 'TEAMS_TIMEOUT') {
          setPickerError('Channels could not be loaded due to a timeout.');
        } else {
          setPickerError('Channels could not be loaded for that team.');
        }
      } finally {
        if (!cancelled) setChannelsLoading(false);
      }
    }
    fetchChannels();
    return () => { cancelled = true; };
  }, [boardId, isAdmin, selectedTeamId]);

  // ---------------------------------------------------------------------------
  // Save board details
  // ---------------------------------------------------------------------------

  const handleSaveBoard = useCallback(async () => {
    const updates: Record<string, unknown> = {};
    if (boardTitle !== title) updates.title = boardTitle;
    if (boardDescription !== description) updates.description = boardDescription;
    if (boardColor !== color) updates.color = boardColor;

    if (Object.keys(updates).length === 0) return;

    try {
      await api.put(`/v1/boards/${boardId}`, updates);
      onBoardUpdated(updates as Partial<{ title: string; description: string; color: string }>);
    } catch {
      // Save failed — could show error
    }
  }, [boardId, boardTitle, boardDescription, boardColor, title, description, color, onBoardUpdated]);

  // ---------------------------------------------------------------------------
  // Label CRUD
  // ---------------------------------------------------------------------------

  const handleCreateLabel = useCallback(async () => {
    const trimmed = labelName.trim();
    if (!trimmed) return;

    const newLabel: BoardLabel = {
      labelId: `label-${crypto.randomUUID()}`,
      name: trimmed,
      color: labelColor,
    };

    const updatedLabels = [...labels, newLabel];
    try {
      await api.put(`/v1/boards/${boardId}`, { labels: updatedLabels });
      onBoardUpdated({ labels: updatedLabels });
      setLabelName('');
      setLabelColor(LABEL_COLORS[0]!.value);
      setShowCreateLabel(false);
    } catch {
      // Creation failed
    }
  }, [boardId, labels, labelName, labelColor, onBoardUpdated]);

  const handleUpdateLabel = useCallback(async (labelId: string) => {
    const trimmed = labelName.trim();
    if (!trimmed) return;

    const updatedLabels = labels.map((l) =>
      l.labelId === labelId ? { ...l, name: trimmed, color: labelColor } : l,
    );

    try {
      await api.put(`/v1/boards/${boardId}`, { labels: updatedLabels });
      onBoardUpdated({ labels: updatedLabels as BoardLabel[] });
      setEditingLabelId(null);
      setLabelName('');
    } catch {
      // Update failed
    }
  }, [boardId, labels, labelName, labelColor, onBoardUpdated]);

  const startEditLabel = (label: BoardLabel) => {
    setEditingLabelId(label.labelId);
    setLabelName(label.name);
    setLabelColor(label.color);
  };

  // ---------------------------------------------------------------------------
  // Teams config actions
  // ---------------------------------------------------------------------------

  const handleTeamsSave = useCallback(async () => {
    if (!selectedTeamId || !selectedChannelId) {
      setTeamsMessage({ type: 'error', text: 'Please select both a team and a channel.' });
      return;
    }
    setTeamsSaving(true);
    setTeamsMessage(null);
    try {
      const result = await api.put<TeamsConfigResponse>(`/v1/boards/${boardId}/teams-config`, {
        teamId: selectedTeamId,
        teamDisplayName: selectedTeamName,
        channelId: selectedChannelId,
        channelDisplayName: selectedChannelName,
      });
      setTeamsConfig(result);
      setSelectedTeamId(result.teamId ?? '');
      setSelectedTeamName(result.teamDisplayName ?? '');
      setSelectedChannelId(result.channelId ?? '');
      setSelectedChannelName(result.channelDisplayName ?? '');
      setTeamsMessage({ type: 'success', text: 'Teams channel configuration saved successfully.' });
    } catch (err: unknown) {
      const apiErr = err && typeof err === 'object' && 'message' in err
        ? (err as { message: string })
        : null;
      setTeamsMessage({ type: 'error', text: apiErr?.message ?? 'Failed to save Teams configuration.' });
    } finally {
      setTeamsSaving(false);
    }
  }, [boardId, selectedTeamId, selectedTeamName, selectedChannelId, selectedChannelName]);

  const handleTeamsTest = useCallback(async () => {
    setTeamsTesting(true);
    setTeamsMessage(null);
    try {
      const result = await api.post<{ success: boolean; message: string }>(`/v1/boards/${boardId}/teams-config/test`);
      if (result.success) {
        setTeamsMessage({ type: 'success', text: result.message });
      } else {
        setTeamsMessage({ type: 'error', text: result.message });
      }
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'message' in err
        ? (err as { message: string }).message
        : 'Failed to test Teams connection.';
      setTeamsMessage({ type: 'error', text: message });
    } finally {
      setTeamsTesting(false);
    }
  }, [boardId]);

  const handleTeamsRemove = useCallback(async () => {
    setTeamsSaving(true);
    setTeamsMessage(null);
    try {
      await api.del(`/v1/boards/${boardId}/teams-config`);
      setSelectedTeamId('');
      setSelectedTeamName('');
      setSelectedChannelId('');
      setSelectedChannelName('');
      // Re-fetch to get the new source (global_default or none)
      const config = await api.get<TeamsConfigResponse>(`/v1/boards/${boardId}/teams-config`);
      setTeamsConfig(config);
      setTeamsMessage({ type: 'success', text: 'Board-level Teams configuration removed.' });
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'message' in err
        ? (err as { message: string }).message
        : 'Failed to remove Teams configuration.';
      setTeamsMessage({ type: 'error', text: message });
    } finally {
      setTeamsSaving(false);
    }
  }, [boardId]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <>
      {/* Backdrop — click outside panel to close */}
      <div
        className="fixed inset-0 z-30"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="fixed inset-y-0 right-0 bg-card text-card-foreground border-l border-border w-80 overflow-y-auto p-4 shadow-xl z-40" role="complementary" aria-label="Board settings">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-card-foreground">Board Settings</h2>
        <button
          onClick={onClose}
          aria-label="Close settings"
          className="p-1 rounded hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
        >
          ✕
        </button>
      </div>

      {/* Board Title */}
      <section className="mb-4">
        <label htmlFor="board-title" className="text-xs font-medium text-muted-foreground uppercase block mb-1">
          Title
        </label>
        <input
          id="board-title"
          type="text"
          value={boardTitle}
          onChange={(e) => setBoardTitle(e.target.value)}
          className="w-full border border-border rounded-md px-3 py-1.5 text-sm bg-card text-card-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </section>

      {/* Board Description */}
      <section className="mb-4">
        <label htmlFor="board-description" className="text-xs font-medium text-muted-foreground uppercase block mb-1">
          Description
        </label>
        <textarea
          id="board-description"
          value={boardDescription}
          onChange={(e) => setBoardDescription(e.target.value)}
          rows={3}
          className="w-full border border-border rounded-md px-3 py-1.5 text-sm bg-card text-card-foreground resize-none focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </section>

      {/* Board Color */}
      <section className="mb-4">
        <label htmlFor="board-color" className="text-xs font-medium text-muted-foreground uppercase block mb-1">
          Board Color
        </label>
        <input
          id="board-color"
          type="color"
          value={boardColor}
          onChange={(e) => setBoardColor(e.target.value)}
          className="w-12 h-8 border border-border rounded cursor-pointer"
        />
      </section>

      {/* Save button */}
      <button
        onClick={handleSaveBoard}
        className="w-full bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm mb-6 hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring"
      >
        Save Changes
      </button>

      {/* Labels Section */}
      <section className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-medium text-muted-foreground uppercase">Labels</h3>
          <button
            onClick={() => { setShowCreateLabel(true); setLabelName(''); setLabelColor(LABEL_COLORS[0]!.value); }}
            className="text-xs text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-ring rounded"
            aria-label="Create label"
          >
            + Create Label
          </button>
        </div>

        {/* Existing labels */}
        <div className="space-y-2">
          {labels.map((label) => (
            <div key={label.labelId} className="flex items-center gap-2">
              {editingLabelId === label.labelId ? (
                <div className="flex-1 space-y-2">
                  <input
                    type="text"
                    value={labelName}
                    onChange={(e) => setLabelName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleUpdateLabel(label.labelId);
                      if (e.key === 'Escape') setEditingLabelId(null);
                    }}
                    className="w-full border border-border rounded-md px-2 py-1 text-sm bg-card text-card-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    autoFocus
                  />
                  <ColorPicker selected={labelColor} onSelect={setLabelColor} />
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleUpdateLabel(label.labelId)}
                      className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded-md"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingLabelId(null)}
                      className="text-xs text-muted-foreground px-2 py-1"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <span
                    className="w-4 h-4 rounded-sm"
                    style={{ backgroundColor: label.color }}
                    aria-hidden="true"
                  />
                  <span className="flex-1 text-sm text-card-foreground">{label.name}</span>
                  <button
                    onClick={() => startEditLabel(label)}
                    className="text-xs text-muted-foreground hover:text-card-foreground"
                    aria-label={`Edit label: ${label.name}`}
                  >
                    Edit
                  </button>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Create label form */}
        {showCreateLabel && (
          <div className="mt-3 border border-border rounded-md p-3 bg-muted/30 space-y-2">
            <label htmlFor="new-label-name" className="text-xs text-muted-foreground block">Label name</label>
            <input
              id="new-label-name"
              type="text"
              value={labelName}
              onChange={(e) => setLabelName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateLabel();
                if (e.key === 'Escape') setShowCreateLabel(false);
              }}
              placeholder="Enter label name..."
              className="w-full border border-border rounded-md px-2 py-1 text-sm bg-card text-card-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              autoFocus
            />
            <ColorPicker selected={labelColor} onSelect={setLabelColor} />
            <div className="flex gap-2">
              <button
                onClick={handleCreateLabel}
                className="text-xs bg-primary text-primary-foreground px-3 py-1 rounded-md"
              >
                Create
              </button>
              <button
                onClick={() => setShowCreateLabel(false)}
                className="text-xs text-muted-foreground px-3 py-1"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Custom Fields Section (admin only) */}
      <CustomFieldsAdmin boardId={boardId} />

      {/* Teams Notifications Section */}
      <section className="mb-6">
        <h3 className="text-xs font-medium text-muted-foreground uppercase mb-2">Teams Notifications</h3>

        {/* Permission guidance */}
        <div className="text-xs text-muted-foreground bg-muted/30 border border-border rounded-md p-3 mb-3 space-y-1">
          <p className="font-medium">Prerequisites:</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li><code className="text-xs">ChannelMessage.Send</code> application permission (requires admin consent)</li>
            <li>CoreFlow app installed in the target Microsoft Team</li>
          </ul>
          <p className="mt-2">
            <a
              href="https://learn.microsoft.com/en-us/graph/permissions-reference"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Microsoft Graph permissions docs
            </a>
            {' · '}
            <a
              href="https://learn.microsoft.com/en-us/microsoftteams/teams-app-setup-policies"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Installing apps in Teams
            </a>
          </p>
        </div>

        {/* Current configuration display */}
        {teamsConfig && (
          <div className="mb-3">
            <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${
              teamsConfig.source === 'board_override'
                ? 'bg-primary/10 text-primary'
                : teamsConfig.source === 'global_default'
                ? 'bg-muted text-muted-foreground'
                : 'bg-destructive/10 text-destructive'
            }`}>
              {teamsConfig.source === 'board_override' && 'Board override'}
              {teamsConfig.source === 'global_default' && 'Using global default'}
              {teamsConfig.source === 'none' && 'Not configured'}
            </span>

            {/* Show current team → channel when configured with display names */}
            {teamsConfig.source === 'board_override' && teamsConfig.teamDisplayName && teamsConfig.channelDisplayName && (
              <p className="text-xs text-muted-foreground mt-1">
                Current: <span className="font-medium text-card-foreground">{teamsConfig.teamDisplayName}</span>
                {' → '}
                <span className="font-medium text-card-foreground">#{teamsConfig.channelDisplayName}</span>
              </p>
            )}

            {/* Show raw IDs for legacy configs */}
            {teamsConfig.source === 'board_override' && teamsConfig.legacy && (
              <div className="mt-1 space-y-1">
                <p className="text-xs text-muted-foreground">
                  Team: <span className="font-mono">{teamsConfig.teamId}</span>
                </p>
                <p className="text-xs text-muted-foreground">
                  Channel: <span className="font-mono">{teamsConfig.channelId}</span>
                </p>
                <p className="text-xs text-amber-600 font-medium mt-1">
                  ⚠️ Legacy config detected — re-select using the picker below to upgrade
                </p>
              </div>
            )}
          </div>
        )}

        {/* Picker error (integration not configured, auth error, etc.) */}
        {pickerError && (
          <div className="text-xs p-2 rounded-md bg-destructive/10 text-destructive mb-3">
            {pickerError}
          </div>
        )}

        {/* Admin controls: Team and Channel pickers */}
        {isAdmin && !pickerError && (
          <div className="space-y-3 mb-3">
            {/* Team selector */}
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Team</label>
              <Select
                value={selectedTeamId}
                onValueChange={(value) => {
                  setSelectedTeamId(value);
                  const team = availableTeams.find((t) => t.id === value);
                  setSelectedTeamName(team?.displayName ?? '');
                  // Reset channel selection when team changes
                  setSelectedChannelId('');
                  setSelectedChannelName('');
                  setAvailableChannels([]);
                }}
                disabled={teamsLoading}
              >
                <SelectTrigger className="w-full h-8 text-sm" aria-label="Select a team">
                  <SelectValue placeholder={teamsLoading ? 'Loading teams...' : 'Select a team...'} />
                </SelectTrigger>
                <SelectContent>
                  {teamsLoading ? (
                    <SelectItem value="__loading" disabled>Loading...</SelectItem>
                  ) : availableTeams.length === 0 ? (
                    <SelectItem value="__empty" disabled>No teams available</SelectItem>
                  ) : (
                    availableTeams.map((team) => (
                      <SelectItem key={team.id} value={team.id}>
                        {team.displayName}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Channel selector */}
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Channel</label>
              <Select
                value={selectedChannelId}
                onValueChange={(value) => {
                  setSelectedChannelId(value);
                  const channel = availableChannels.find((c) => c.id === value);
                  setSelectedChannelName(channel?.displayName ?? '');
                }}
                disabled={!selectedTeamId || channelsLoading}
              >
                <SelectTrigger className="w-full h-8 text-sm" aria-label="Select a channel">
                  <SelectValue placeholder={
                    !selectedTeamId
                      ? 'Select a team first...'
                      : channelsLoading
                      ? 'Loading channels...'
                      : 'Select a channel...'
                  } />
                </SelectTrigger>
                <SelectContent>
                  {channelsLoading ? (
                    <SelectItem value="__loading" disabled>Loading...</SelectItem>
                  ) : availableChannels.length === 0 ? (
                    <SelectItem value="__empty" disabled>No channels available</SelectItem>
                  ) : (
                    availableChannels.map((channel) => (
                      <SelectItem key={channel.id} value={channel.id}>
                        {channel.displayName}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Non-admin read-only view */}
        {!isAdmin && teamsConfig && teamsConfig.source === 'board_override' && (
          <div className="text-xs text-muted-foreground mb-3 space-y-1">
            {teamsConfig.teamDisplayName && teamsConfig.channelDisplayName ? (
              <p>
                Notifications go to <span className="font-medium text-card-foreground">{teamsConfig.teamDisplayName}</span>
                {' → '}
                <span className="font-medium text-card-foreground">#{teamsConfig.channelDisplayName}</span>
              </p>
            ) : (
              <>
                <p>Team: <span className="font-mono">{teamsConfig.teamId}</span></p>
                <p>Channel: <span className="font-mono">{teamsConfig.channelId}</span></p>
              </>
            )}
          </div>
        )}

        {/* Action buttons (admin only) */}
        {isAdmin && (
          <div className="flex flex-wrap gap-2 mb-2">
            <button
              onClick={handleTeamsSave}
              disabled={teamsSaving || teamsTesting || !selectedTeamId || !selectedChannelId}
              className="bg-primary text-primary-foreground px-3 py-1.5 rounded-md text-xs hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
            >
              {teamsSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={handleTeamsTest}
              disabled={teamsSaving || teamsTesting || teamsConfig?.source === 'none'}
              className="border border-border text-card-foreground px-3 py-1.5 rounded-md text-xs hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
            >
              {teamsTesting ? 'Testing...' : 'Test Connection'}
            </button>
            {teamsConfig?.source === 'board_override' && (
              <button
                onClick={handleTeamsRemove}
                disabled={teamsSaving || teamsTesting}
                className="border border-destructive/50 text-destructive px-3 py-1.5 rounded-md text-xs hover:bg-destructive/10 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              >
                Remove
              </button>
            )}
          </div>
        )}

        {/* Success/error feedback */}
        {teamsMessage && (
          <div className={`text-xs p-2 rounded-md ${
            teamsMessage.type === 'success'
              ? 'bg-green-500/10 text-green-700'
              : 'bg-destructive/10 text-destructive'
          }`}>
            {teamsMessage.text}
          </div>
        )}
      </section>

      {/* Notification Templates Section (admin only) */}
      <section className="mb-6">
        <details className="group">
          <summary className="cursor-pointer text-xs font-medium text-muted-foreground uppercase mb-2 hover:text-foreground">
            Notification Templates
          </summary>
          <div className="mt-2">
            <NotificationTemplateEditor boardId={boardId} />
          </div>
        </details>
      </section>

      {/* Danger Zone — Archive Board */}
      <section className="pt-4 border-t border-border">
        <h3 className="text-xs font-medium text-destructive uppercase mb-2">Danger Zone</h3>

        {!showArchiveConfirm ? (
          <button
            onClick={() => setShowArchiveConfirm(true)}
            className="w-full border border-destructive/50 text-destructive bg-destructive/5 px-4 py-2 rounded-md text-sm hover:bg-destructive/10 focus:outline-none focus:ring-2 focus:ring-ring"
          >
            Archive this board
          </button>
        ) : (
          <div className="border border-destructive/30 rounded-md p-3 bg-destructive/5 space-y-3">
            <p className="text-sm text-card-foreground">
              <strong>You're about to archive this board.</strong> All cards will be preserved but the board
              will no longer appear in your boards list. This can be undone later.
            </p>
            <div className="flex gap-2">
              <button
                onClick={async () => {
                  setIsArchiving(true);
                  try {
                    await api.put(`/v1/boards/${boardId}`, { archived: true });
                    onBoardArchived?.();
                  } catch (err: unknown) {
                    setIsArchiving(false);
                    setShowArchiveConfirm(false);
                    // Surface the error so the user knows it failed
                    const message = err instanceof Error ? err.message : 'Failed to archive board';
                    alert(message);
                  }
                }}
                disabled={isArchiving}
                className="flex-1 bg-destructive text-destructive-foreground px-3 py-2 rounded-md text-sm hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              >
                {isArchiving ? 'Archiving...' : 'Yes, archive board'}
              </button>
              <button
                onClick={() => setShowArchiveConfirm(false)}
                className="flex-1 border border-border text-muted-foreground px-3 py-2 rounded-md text-sm hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Color Picker sub-component
// ---------------------------------------------------------------------------

interface ColorPickerProps {
  readonly selected: string;
  readonly onSelect: (color: string) => void;
}

function ColorPicker({ selected, onSelect }: ColorPickerProps): React.ReactElement {
  return (
    <div className="flex flex-wrap gap-1" role="radiogroup" aria-label="Label color">
      {LABEL_COLORS.map((color) => (
        <button
          key={color.value}
          onClick={() => onSelect(color.value)}
          className={`w-6 h-6 rounded-sm border-2 ${selected === color.value ? 'border-ring' : 'border-transparent'}`}
          style={{ backgroundColor: color.value }}
          aria-label={color.name}
          aria-pressed={selected === color.value}
          role="radio"
          aria-checked={selected === color.value}
        />
      ))}
    </div>
  );
}
